/**
 * Mobile App Redirect Utility
 * Detects mobile devices and redirects to appropriate app store
 * For ChannelplayJobs mobile app
 */

const MOBILE_APP_LINKS = {
  ios: {


    appStore: 'https://apps.apple.com/in/app/channelplay-jobs/id6504976441',
    deepLink: 'cpjobs://open', // From Info.plist CFBundleURLSchemes
  },
  android: {

    playStore: 'https://play.google.com/store/apps/details?id=com.channelplay.jobs',
    deepLink: 'cpjobs://open', // From AndroidManifest.xml intent-filter
  }
};

const WHITE_SCREEN_HTML = `
  <div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:'Inter',sans-serif;text-align:center;padding:24px;background:#ffffff;color:#0f172a;">
  </div>
`;

const PLAY_STORE_FALLBACK_HTML = `
  <div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:'Inter',sans-serif;text-align:center;padding:24px;background:#ffffff;color:#0f172a;">
    <div>
      <p style="margin:0;font-size:16px;font-weight:600;">You can close this tab now.</p>
    </div>
  </div>
`;

const clearRedirectArtifacts = () => {
  try {
    sessionStorage.removeItem('pendingJobDetails');
  } catch (_) {}

  try {
    localStorage.removeItem('pendingJobDetails');
  } catch (_) {}
};

const cleanupAndCloseTab = (delay = 300, fallbackHtml = WHITE_SCREEN_HTML) => {
  clearRedirectArtifacts();
  setTimeout(() => {
    try {
      window.open('', '_self');
      window.close();
    } catch (error) {
      console.warn('[MobileRedirect] Failed to close tab directly:', error);
    } finally {
      if (fallbackHtml && document.visibilityState !== 'hidden') {
        document.body.innerHTML = fallbackHtml;
      }
    }
  }, delay);
};


/**
 * Detects if the current device is iOS (iPhone, iPad, iPod)
 */
export const isIOS = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
};

/**
 * Detects if the current device is Android
 */
export const isAndroid = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /android/.test(userAgent);
};

/**
 * Detects if the current device is a mobile device (iOS or Android)
 */
export const isMobileDevice = () => {
  return isIOS() || isAndroid();
};

/**
 * Check if URL path indicates app redirect should happen
 * Examples:
 *   - https://jobs.channelplay.in/app → Redirect to app
 *   - https://jobs.channelplay.in/app/jobs → Redirect to app
 *   - https://jobs.channelplay.in → Show website (no redirect)
 *   - https://jobs.channelplay.in/jobs → Show website (no redirect)
 * 
 * @returns {boolean} - True if should redirect to app, false otherwise
 */
const shouldRedirectToApp = () => {
  const path = window.location.pathname;
  
  // Check if path starts with /app
  if (path.startsWith('/app')) {
    console.log('[MobileRedirect] /app path detected - Will redirect to app');
    return true;
  }
  
  // Default: show website (no redirect)
  console.log('[MobileRedirect] Regular path - Showing website');
  return false;
};

/**
 * Force redirect to mobile app (bypasses path check)
 * Used when redirectInApp=true parameter is present
 * 
 * @param {string} actualUrl - The actual URL from getActualUrl API response (e.g., "/ApplyForJob?budgetCityId=123...")
 * @returns {boolean} - Returns true if redirect was triggered, false otherwise
 */
export const forceRedirectToMobileApp = (actualUrl = '') => {
  if (!isMobileDevice()) {
    console.log('[MobileRedirect] Desktop device - No redirect');
    return false;
  }

  // Build deep link with actual URL data
  // Format: channelplayjobs://ApplyJobFromWhatsAppNotification?budgetCityId=123&budgetId=456...
  let deepLinkUrl = '';
  
  if (actualUrl) {
    try {
      // Parse the actual URL to extract parameters
      const url = new URL(actualUrl, window.location.origin);
      const params = new URLSearchParams(url.search);
      
      // Build deep link with route and parameters
      if (url.pathname.includes('/ApplyForJob')) {
        deepLinkUrl = `channelplayjobs://ApplyJobFromWhatsAppNotification?${params.toString()}`;
        console.log('[MobileRedirect] Built deep link with actual URL data:', deepLinkUrl);
      } else {
        // Fallback to platform-specific generic deep link
        deepLinkUrl = isIOS() ? MOBILE_APP_LINKS.ios.deepLink : MOBILE_APP_LINKS.android.deepLink;
      }
    } catch (e) {
      console.warn('[MobileRedirect] Failed to parse actual URL, using default deep link:', e);
      deepLinkUrl = isIOS() ? MOBILE_APP_LINKS.ios.deepLink : MOBILE_APP_LINKS.android.deepLink;
    }
  } else {
    deepLinkUrl = isIOS() ? MOBILE_APP_LINKS.ios.deepLink : MOBILE_APP_LINKS.android.deepLink;
  }

  if (isIOS()) {
    // iOS: Try deep link first, then App Store
    console.log('[MobileRedirect] iOS detected - Attempting to open app via deep link:', deepLinkUrl);
    
    // Track if page loses visibility (app opened)
    let appOpened = false;
    const startTime = Date.now();
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        appOpened = true;
        console.log('[MobileRedirect] App opened successfully');
        cleanupAndCloseTab(200);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Try to open app directly (works better than iframe on iOS)
    window.location.href = deepLinkUrl;
    
    // Set a timer to redirect to App Store if app doesn't open
    setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // If page didn't lose focus and less than 2.5 seconds passed, app is not installed
      if (!appOpened && (Date.now() - startTime < 2500)) {
        console.log('[MobileRedirect] App not installed - Redirecting to App Store');
        window.location.href = MOBILE_APP_LINKS.ios.appStore;
        cleanupAndCloseTab(500);
      } else if (appOpened) {
        console.log('[MobileRedirect] App opened successfully - Closing tab');
        cleanupAndCloseTab(200);
      }
    }, 2000);
    
    return true;
  } 
  
  if (isAndroid()) {
    // Android: Try deep link first, detect if app opens, cancel fallback if it does
    console.log('[MobileRedirect] Android detected - Attempting to open app via deep link:', deepLinkUrl);
    
    // Track if page loses visibility (app opened)
    let appOpened = false;
    let fallbackTimer = null;
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        appOpened = true;
        console.log('[MobileRedirect] ✅ App opened successfully - Canceling fallback');
        
        // Clear the fallback timer since app opened
        if (fallbackTimer) {
          clearTimeout(fallbackTimer);
          fallbackTimer = null;
        }
        
        cleanupAndCloseTab(200);
      }
    };
    
    const handleBlur = () => {
      // Additional check - page lost focus
      if (!appOpened) {
        appOpened = true;
        console.log('[MobileRedirect] ✅ Page lost focus - App likely opened');
        
        // Clear fallback timer
        if (fallbackTimer) {
          clearTimeout(fallbackTimer);
          fallbackTimer = null;
        }
        
        cleanupAndCloseTab(200);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    
    // Attempt to open the app via deep link
    window.location.href = deepLinkUrl;

    // After 2.2 seconds, check if app opened (gives deep link more time before falling back)
    fallbackTimer = setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      
      if (appOpened || document.visibilityState === 'hidden') {
        console.log('[MobileRedirect] Visibility hidden just before fallback - skipping Play Store redirect');
        return;
      }
      
      console.log('[MobileRedirect] ❌ App not installed - Redirecting to Play Store via https');
      window.location.href = 'https://play.google.com/store/apps/details?id=com.channelplay.jobs&referrer=channelplayjobs-shortlink';
      cleanupAndCloseTab(500, PLAY_STORE_FALLBACK_HTML);
    }, 2200);
    
    return true;
  }

  return false;
};

/**
 * Redirects mobile users to the appropriate app store or opens the app if installed
 * - Only redirects if URL path is /app or /app/*
 * - Regular paths (/, /jobs, etc.) always show website
 * - Desktop users on /app path are redirected to homepage
 * - iOS users: Attempts deep link first, then App Store if app not installed
 * - Android users: Attempts deep link first, then redirects to Play Store after 2 seconds
 * 
 * @returns {boolean} - Returns true if redirect was triggered, false otherwise
 */
export const redirectToMobileApp = () => {
  // Check if URL path indicates app redirect
  const isAppPath = shouldRedirectToApp();
  
  // If desktop user visits /app path, redirect to homepage
  if (!isMobileDevice() && isAppPath) {
    console.log('[MobileRedirect] Desktop user on /app path - Redirecting to homepage');
    window.location.href = '/';
    return true;
  }
  
  // Only redirect if on mobile device
  if (!isMobileDevice()) {
    console.log('[MobileRedirect] Desktop device - No redirect');
    return false;
  }
  
  // Check if URL path indicates app redirect
  if (!isAppPath) {
    console.log('[MobileRedirect] Regular URL path - Showing website');
    return false;
  }

  if (isIOS()) {
    // iOS: Try deep link first, then App Store
    console.log('[MobileRedirect] iOS detected - Attempting to open app via deep link');
    
    // Track if page loses visibility (app opened)
    let appOpened = false;
    const startTime = Date.now();
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        appOpened = true;
        console.log('[MobileRedirect] App opened successfully');
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Try to open app directly (works better than iframe on iOS)
    window.location.href = MOBILE_APP_LINKS.ios.deepLink;
    
    // Set a timer to redirect to App Store if app doesn't open
    setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // If page didn't lose focus and less than 2.5 seconds passed, app is not installed
      if (!appOpened && (Date.now() - startTime < 2500)) {
        console.log('[MobileRedirect] App not installed - Redirecting to App Store');
        window.location.href = MOBILE_APP_LINKS.ios.appStore;
        
        // Close tab after App Store redirect
        setTimeout(() => {
          try {
            window.close();
          } catch (e) {}
        }, 500);
      } else if (appOpened) {
        console.log('[MobileRedirect] App opened successfully - Closing tab');
        try {
          window.close();
        } catch (e) {}
      }
    }, 2000);
    
    return true;
  } 
  
  if (isAndroid()) {
    // Android: Try deep link first, detect if app opens, cancel fallback if it does
    console.log('[MobileRedirect] Android detected - Attempting to open app via deep link');
    
    // Track if page loses visibility (app opened)
    let appOpened = false;
    let fallbackTimer = null;
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        appOpened = true;
        console.log('[MobileRedirect] ✅ App opened successfully - Canceling fallback');
        
        // Clear fallback timer
        if (fallbackTimer) {
          clearTimeout(fallbackTimer);
          fallbackTimer = null;
        }
        
        cleanupAndCloseTab(200);
      }
    };
    
    const handleBlur = () => {
      if (!appOpened) {
        appOpened = true;
        console.log('[MobileRedirect] ✅ Page lost focus - App likely opened');
        
        // Clear fallback timer
        if (fallbackTimer) {
          clearTimeout(fallbackTimer);
          fallbackTimer = null;
        }
        
        cleanupAndCloseTab(200);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    
    // Attempt to open the app via deep link
    window.location.href = MOBILE_APP_LINKS.android.deepLink;

    // After 2.2 seconds, check if app opened (gives deep link more time before falling back)
    fallbackTimer = setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      
      if (!appOpened) {
        console.log('[MobileRedirect] ❌ App not installed - Redirecting to Play Store via https');
        window.location.href = 'https://play.google.com/store/apps/details?id=com.channelplay.jobs&referrer=channelplayjobs-shortlink';
        cleanupAndCloseTab(500, PLAY_STORE_FALLBACK_HTML);
      } else {
        console.log('[MobileRedirect] ✅ App opened - No fallback needed');
      }
    }, 2200);
    
    return true;
  }

  return false;
};

/**
 * Checks if mobile app redirect should be triggered
 * Can be used to conditionally show UI or trigger redirect
 */
export const shouldRedirectToMobileApp = () => {
  return isMobileDevice();
};

/**
 * Get device type for analytics or conditional rendering
 */
export const getDeviceType = () => {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  return 'desktop';
};

// Default export
const mobileRedirect = {
  redirectToMobileApp,
  forceRedirectToMobileApp,
  shouldRedirectToMobileApp,
  getDeviceType,
  isIOS,
  isAndroid,
  isMobileDevice,
};

export default mobileRedirect;
